import { inject, Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import {
  ApplicationsAction, AssignApplicationsToCohort, ClearApplications, DeleteReferees, GetAdminData, GetApplicationRecord, SetNeedClinicalDirector, GetExportedApplications, GetApplications, GetFlags, GetMMIApplications, GetMMIInterviewees, GetProApplications, InitiateApplication, MarkAttendance, MarkInReviewApplication, SaveAdminStep, SaveFlag, SaveStep, SelectProApplication, SelectProReviewApplication, SetApplicationDecision, SubmitApplication, UnSubmitApplication, GetFlagDetails, UpdateFlagResolution, GetAllProExamApplications, UnSubmitPhase2Application, GetAllProExamApplicationsSilent,
  ClearAllApplicationSteps,
  GetAllApplicationSteps,
  GetEligibilityDetails,
  SavePhaseTwoStep,
  OpenPhase2Application,
  SubmitPro2Application,
  SaveSinglePhaseTwoStep,
  GetSingleStepApplication,
  UpdateSinglePhaseTwoStep,
  GetPhase2ApplicationRecord,
  SavePhase2AdminStep,
  AddReferenceNote,
  ResendSingleReferenceEmail,
  ResendReferenceEmail,
  GetAdminApplicationByNo,
  ResendSingleRotationEquivalences,
  ClearSelectedProApplication,
  SetPendingApplicationLanguage,
} from './applications.actions';
import { ApplicationService } from '@tsin-core/services/http/application.service';
import { LoadingState } from '@tsin-core/entities';
import { catchError, map, tap } from 'rxjs';
import { IAttendance, IMMIInterviewees } from '../administration/administration.model';
import { AuthState } from '../auth.state';
import { PreScreeningState } from '../pre-screening/pre-screening.state';
import { determineEligibility } from '@tsin-shared/utils/eligibility';
import { ProExamState } from '../pro-exam/pro-exam.state';
import { Phase2ApplicationAdminSteps, ProApplicationReview, ProApplicationReviewStep, ProApplicationStatusEnum, ProApplicationStep, ProUserApplication } from './applications.model';

export class ApplicationsStateModel {
  public applications: ProUserApplication[] = [];
  public submittedApplications: ProUserApplication[] = [];
  public selectedAdminData: any = null;
  public applicationSteps: any[] = [];
  public selectedApplication: ProUserApplication | null = null;
  public reviewApplication: ProApplicationReview | null = null;
  public singleCreatedReferee: any = null;
  public needClinicalDirectorReview: boolean = false;
  public mmiApplications: any[] = [];
  public exportedApplications: any = null;
  public eligibilityDetails: any = null;
  public mmiInterviewees: IMMIInterviewees[] = [];
  public reviewPhase2: Phase2ApplicationAdminSteps | null = null;
  public loading!: LoadingState;
  public flags: any[] = [];
  /** Language chosen by the applicant at the beginning-page, before the application is created. */
  public pendingLanguage: 'en' | 'fr' = 'en';
}

const defaults = {
  selectedApplication: null,
  selectedAdminData: null,
  reviewApplication: null,
  needClinicalDirectorReview: false,
  singleCreatedReferee: null,
  applicationSteps: [],
  applications: [],
  submittedApplications: [],
  eligibilityDetails: null,
  exportedApplications: null,
  mmiApplications: [],
  mmiInterviewees: [],
  reviewPhase2: null,
  flags: [],
  loading: LoadingState.notLoading,
  pendingLanguage: 'en' as 'en' | 'fr',
};

@State<ApplicationsStateModel>({
  name: 'applicationState',
  defaults
})

@Injectable()
export class ApplicationsState {

  applicationService: ApplicationService = inject(ApplicationService);
  private store = inject(Store); // Inject the store

  @Selector()
  static getApplications(state: ApplicationsStateModel) {
    return state.applications;
  }

  @Selector()
  static getSubmittedApplications(state: ApplicationsStateModel) {
    return state.submittedApplications;
  }

  @Selector()
  static getFlags(state: ApplicationsStateModel) {
    return state.flags;
  }

  @Selector()
  static getSelectedApplication(state: ApplicationsStateModel) {
    return state.selectedApplication;
  }

  @Selector()
  static getReviewApplication(state: ApplicationsStateModel) {
    return state.reviewApplication;
  }

  @Selector()
  static canContinueApplication(state: ApplicationsStateModel) {
    return state.selectedApplication?.status === ProApplicationStatusEnum.InProgress || state.selectedApplication?.phase2Status === ProApplicationStatusEnum.InProgress;
  }

  @Selector()
  static canSubmitApplication(state: ApplicationsStateModel) {
    return state.selectedApplication?.steps.every(step => step.finished) ?? false;
  }

  @Selector()
  static getApplicationSteps(state: ApplicationsStateModel) {
    return state.applicationSteps;
  }

  @Selector()
  static continueStepName(state: ApplicationsStateModel): string | null {
    const application = state.selectedApplication;

    if (application == null) {
      return null;
    }

    const step = application.steps.find(s => !s.finished);
    if (step == null) {
      const lastStep = application.steps[application.steps.length - 1];
      if (lastStep == null) {
        return 'personal-information';
        // return 'pre-screening';
      } else {
        return lastStep.name;
      }
    }

    return step.name;
  }

  @Selector()
  static continuePhase2StepName(state: ApplicationsStateModel): string | null {

    const application = state.selectedApplication;

    const _steps = [
      { name: 'personal-information', id: 1 },
      { name: 'certifications-and-qualifications', id: 2 },
      { name: 'language-proficiency', id: 3 },
      { name: 'postgraduate-training', id: 4 },
      { name: 'rotation-equivalencies', id: 5 },
      { name: 'independent-practice-clinical-experience', id: 6 },
      { name: 'clinical-practice-settings', id: 7 },
      { name: 'ip-patient-population', id: 8 },
      { name: 'currency-practice-clinical-experience', id: 9 },
      { name: 'currency-practice-settings', id: 10 },
      { name: 'currency-of-practice-patient-populations', id: 11 },
      { name: 'references', id: 12 },
      { name: 'information-sharing', id: 13 },
    ];

    // auto select Exam
    if (application == null) {
      return null;
    }

    const exam = application?.phase2Exam;


    const phase2AppSteps = application?.phase2ApplicationSteps == null ? [] : Object.values(application?.phase2ApplicationSteps);

    const phase2Step: any = phase2AppSteps?.find((s: any) => s.name && !s?.completed);



    if (phase2Step == undefined) {
      const lastStep: any = phase2AppSteps.reverse().find((item: any) => item.completed === true);
      if (lastStep == undefined) {
        return 'personal-information';
      } else {
        return lastStep.name;
      }
    }
    return phase2Step.name;
  }


  @Selector()
  static getStepData(state: ApplicationsStateModel) {
    return (stepName: string) => {
      return state.selectedApplication?.steps.find(step => step.name === stepName)?.data;
    };
  }

  @Selector()
  static getAdminStepData(state: ApplicationsStateModel): ProApplicationReviewStep[] {
    return state.reviewApplication?.application ?? [];
  }

  @Selector()
  static getStepTwoData(state: ApplicationsStateModel) {
    return (stepName: string) => {
      if (state.selectedApplication == null) {
        return null;
      }

      if (state.selectedApplication?.phase2ApplicationSteps == undefined || state.selectedApplication?.phase2ApplicationSteps[stepName as keyof typeof state.selectedApplication.phase2ApplicationSteps] == undefined)
        return null;

      return state.selectedApplication.phase2ApplicationSteps[stepName as keyof typeof state.selectedApplication.phase2ApplicationSteps];
    };
  }

  @Selector()
  static getPhase2ReviewStep(state: ApplicationsStateModel) {
    return (stepName: string) => {
      if (state.selectedApplication == null) {
        return;
      }

      const reviewStep = state.selectedApplication.phase2ApplicationSteps;

      if (reviewStep[stepName as keyof typeof reviewStep] == undefined) {
        return;
      }

      return reviewStep[stepName as keyof typeof reviewStep];
    }
  }


  @Selector()
  static getPhase2ReviewData(state: ApplicationsStateModel) {
    if (state.reviewPhase2 == null) {
      return;
    }
    return state.reviewPhase2;
  }


  @Selector()
  static getMMIApplications(state: ApplicationsStateModel) {
    return state.mmiApplications;
  }

  @Selector()
  static getMMIInterviewees(state: ApplicationsStateModel) {
    return state.mmiInterviewees;
  }

  @Selector()
  static getLoading(state: ApplicationsStateModel) {
    return state.loading;
  }

  @Selector()
  static getAdminData(state: ApplicationsStateModel) {
    return state.selectedAdminData;
  }

  @Selector()
  static getPendingLanguage(state: ApplicationsStateModel): 'en' | 'fr' {
    return state.pendingLanguage;
  }

  @Action(ApplicationsAction)
  add({ getState, setState }: StateContext<ApplicationsStateModel>, { payload }: ApplicationsAction) {
    const state = getState();
    // setState({ ...state, applications: [...state.applications, payload] });
  }

  @Action(GetApplications)
  getApplications({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetApplications) {
    const state = getState();
    // setState({ ...state, loading: LoadingState.loadingList });
    patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getApplications(action.payload).pipe(tap((applications: { data: any[] }) => {
      patchState({ applications: applications.data, loading: LoadingState.notLoading });
    }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetMMIApplications)
  getMMIApplications({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetMMIApplications) {
    const state = getState();
    // setState({ ...state, loading: LoadingState.loadingList });
    patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getMMIApplications(action.payload).pipe(tap((applications: { data: any[] }) => {
      // console.log('Get MMI List', applications.data);
      patchState({ mmiApplications: applications.data, loading: LoadingState.notLoading });
    }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetMMIInterviewees)
  getMMIInterviewees({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetMMIInterviewees) {
    const state = getState();
    // setState({ ...state, loading: LoadingState.loadingList });
    patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getMMIInterviewees(action.payload).pipe(tap((interviewees: IMMIInterviewees[]) => {
      // console.log('Get MMI Interviewees', interviewees);
      patchState({ mmiInterviewees: interviewees, loading: LoadingState.notLoading });
    }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(SelectProApplication)
  selectProApplication(ctx: StateContext<ApplicationsStateModel>, action: SelectProApplication | null) {
    ctx.patchState({ selectedApplication: action?.payload });
  }

  @Action(SelectProReviewApplication)
  selectProReviewApplication(ctx: StateContext<ApplicationsStateModel>, action: SelectProReviewApplication) {
    ctx.patchState({ reviewApplication: action.payload });
  }

  @Action(ClearApplications)
  clearApplications({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: ClearApplications) {
    patchState({ applications: [] });
  }

  @Action(AssignApplicationsToCohort)
  assignApplicationsCohort({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: AssignApplicationsToCohort) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService.assignApplicationToMMICohort(action.payload).pipe(
      tap((result: any) => {
        const state = getState();
        // console.log('Result', result);
        patchState({
          mmiApplications: result,
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(MarkAttendance)
  markAttendance({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: MarkAttendance) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService.markAttendance(action.payload).pipe(
      tap((attendance: IAttendance) => {
        const state = getState();
        // console.log('Attendance Result', attendance);
        const updatedInterviewees = state.mmiInterviewees.map(interviewee =>
          interviewee.applicationId === action.payload.applicationId
            ? { ...interviewee, attendance: attendance }
            : interviewee
        );
        patchState({
          mmiInterviewees: updatedInterviewees,
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetProApplications)
  getProApplications({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetProApplications) {
    const state = getState();
    patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getProApplications().pipe(tap((applications: ProUserApplication[]) => {
      patchState({ applications: applications, loading: LoadingState.notLoading });
      const userId = this.store.selectSnapshot(AuthState.getCurrentUser)!.id;
      const selectedApplication = applications.find(app => app?.user === userId && (app.status === ProApplicationStatusEnum.InProgress || app.phase2Status === ProApplicationStatusEnum.InProgress)) || null;
      if (selectedApplication) {
        patchState({ selectedApplication });
      }
    }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(SetPendingApplicationLanguage)
  setPendingApplicationLanguage({ patchState }: StateContext<ApplicationsStateModel>, action: SetPendingApplicationLanguage) {
    patchState({ pendingLanguage: action.payload });
  }

  @Action(InitiateApplication)
  initiateApplication({ getState, patchState }: StateContext<ApplicationsStateModel>, action: InitiateApplication) {
    const state = getState();
    const userId = this.store.selectSnapshot(AuthState.getCurrentUser)!.id;
    const preScreeningData = this.store.selectSnapshot(PreScreeningState.getPreScreening);
    const examId = action.payload.examId;
    // Use the language from the action payload, fall back to the pending language stored when
    // the applicant made their selection on the beginning page.
    const language = action.payload.language ?? state.pendingLanguage ?? 'en';

    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService.initializeApplication(
      examId,
      userId,
      preScreeningData,
      language,
    ).pipe(
      tap((selectedApplication: ProUserApplication) => {
        patchState({ selectedApplication, applications: [...state.applications, selectedApplication], loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(SaveStep)
  saveStep(state: StateContext<ApplicationsStateModel>, action: SaveStep) {

    const { step, data, isFinished } = action.payload;
    const userId = this.store.selectSnapshot(AuthState.getCurrentUser)!.id;
    const preScreeningData = this.store.selectSnapshot(PreScreeningState.getPreScreening);
    const examId = this.store.selectSnapshot(ProExamState.getSingleProExam)!.id;

    const selectedProApplication = state.getState().selectedApplication;

    if (selectedProApplication == null) {
      throw new Error('Select application first before saving step');
    }

    const applicationId = selectedProApplication.id;

    const index = selectedProApplication.steps.findIndex((s: any) => s.name === step);
    if (index === -1) {
      selectedProApplication.steps.push({ name: step, data, finished: isFinished });
    } else {
      selectedProApplication.steps[index].data = data;
      selectedProApplication.steps[index].finished = isFinished;
    }

    const calculatedEligibility = determineEligibility(selectedProApplication, preScreeningData);
    // console.log('eligibility:: info ', calculatedEligibility);
    state.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService.savePhaseOneApplication(userId, applicationId, examId, calculatedEligibility, preScreeningData, selectedProApplication.steps).pipe(
      tap((selectedApplication: any) => {
        state.patchState({ selectedApplication, loading: LoadingState.notLoading });
        // console.log('Step saved successfully:', selectedApplication);
      }),
      catchError((error) => {
        console.error('Error submitting Phase One Application', error);
        state.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(SubmitApplication)
  submitApplication({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: SubmitApplication) {
    const state = getState();
    const applicationId = action.payload;
    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.submitPhaseOneApplication(applicationId).pipe(
      tap((response: { data: ProUserApplication }) => {
        // console.log('Phase One Application submitted successfully', response);
        patchState({ selectedApplication: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        console.error('Error submitting Phase One Application', error);
        throw error;
      })
    );
  }

  @Action(UnSubmitApplication)
  unSubmitApplication({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: UnSubmitApplication) {
    const state = getState();
    const applicationId = action.payload;
    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.unSubmitPhaseOneApplication(applicationId).pipe(
      tap((response: { data: ProUserApplication }) => {
        // console.log('Phase One Application unsubmitted successfully', response);
        patchState({ selectedApplication: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error unSubmitting Phase One Application', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(UnSubmitPhase2Application)
  unSubmitPhase2Application({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: UnSubmitPhase2Application) {
    const state = getState();
    const applicationId = action.payload;
    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.unSubmitPhaseTwoApplication(applicationId).pipe(
      tap((response: { data: ProUserApplication }) => {
        // console.log('Phase Two Application unsubmitted successfully', response);
        patchState({ selectedApplication: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error unSubmitting Phase Two Application', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(MarkInReviewApplication)
  markInReviewApplication({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: MarkInReviewApplication) {
    const state = getState();
    const applicationId = action.payload;
    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.markInReviewApplication(applicationId).pipe(
      tap((response: { data: ProUserApplication }) => {
        // console.log('Phase One Application marked in review successfully', response);
        patchState({ selectedApplication: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error marking Phase One Application in review', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(SaveFlag)
  saveFlag({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: SaveFlag) {
    const state = getState();
    const { applicationNo, message, author } = action.payload;

    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.saveFlag({ applicationNo, message }).pipe(
      tap((response: { data: ProUserApplication }) => {
        // console.log('Application Flag saved successfully', response);
        patchState({ selectedApplication: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error saving Application Flag', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetFlags)
  getFlags({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetFlags) {
    const state = getState();
    const applicationNo = action.payload;

    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.getFlags(applicationNo).pipe(
      tap((response: { data: any[] }) => {
        // console.log('Application Flags retrieved successfully', response);
        // patchState({ selectedApplication: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error retrieving Application Flags', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(SaveAdminStep)
  saveAdminStep({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: SaveAdminStep) {
    const state = getState();
    const { step, appNo, data } = action.payload;
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService.saveAdminStep(step, appNo, data).pipe(
      tap((selectedApplication: any) => {
        // console.log('Admin Step saved successfully', selectedApplication);
        patchState({ selectedApplication, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error saving Admin Step', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(SavePhase2AdminStep)
  savePhase2AdminStep({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: SavePhase2AdminStep) {
    const state = getState();
    const { step, appNo, data } = action.payload;
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService.savePhase2AdminStep(step, appNo, data).pipe(
      tap((response: any) => {
        if (!state.selectedApplication) {
          patchState({ loading: LoadingState.notLoading });
          return;
        }

        this.store.dispatch(new GetAdminApplicationByNo({ applicationNo: appNo }));
        // patchState({ loading: LoadingState.notLoading });

        // const selectedApplication: ProUserApplication = {
        //   ...state.selectedApplication,
        //   phase2ApplicationSteps: {
        //     ...state.selectedApplication.phase2ApplicationSteps,
        //     [step]: response
        //   }
        // };

      }),
      catchError((error: any) => {
        console.error('Error saving Phase 2 Admin Step', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(SetApplicationDecision)
  setApplicationDecision({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: SetApplicationDecision) {
    const state = getState();
    const { applicationNo, examId, decision, comment, conditions, reasons } = action.payload;
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService.setApplicationDecision(applicationNo, examId, decision, comment, conditions, reasons).pipe(
      tap((selectedApplication: any) => {
        // console.log('Application decision set successfully', selectedApplication);
        patchState({ selectedApplication, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error setting Application decision', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }


  @Action(SavePhaseTwoStep)
  savePhaseTwoStep(state: StateContext<ApplicationsStateModel>, action: SavePhaseTwoStep) {

    const { step, data, isFinished } = action.payload;
    const examId = this.store.selectSnapshot(ProExamState.getSingleProExam)!.id;
    const selectedProApplication = state.getState().selectedApplication;

    console.log('Step details', action.payload);

    if (selectedProApplication == null) {
      throw new Error('Select application first before saving step');
    }

    const applicationId = selectedProApplication.id;
    const appExamId = selectedProApplication?.phase2Exam?.id;

    // Check if some steps are in existence
    // selectedProApplication.phase2ApplicationSteps[applicationStepKey]

    if (selectedProApplication == null) {
      throw new Error('Select application first before saving step');
    }


    state.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService.savePhaseTwoApplication({
      id: applicationId,
      applicationNo: selectedProApplication.applicationNo,
      examId: examId ?? appExamId,
      data: action.payload.data,
      step: step,
      completed: action.payload.isFinished ?? false,
    }).pipe(
      tap((selectedApplication: any) => {

        console.log('Phase Two Step saved successfully:', selectedApplication);
        state.patchState({ selectedApplication: selectedApplication, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        console.error('Error submitting Phase One Application', error);
        state.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetSingleStepApplication)
  getSingleStepApplication({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetSingleStepApplication) {
    const state = getState();
    const { step, applicationNo } = action.payload;
    const selectedProApplication = state.selectedApplication;

    if (selectedProApplication == null) {
      throw new Error('Select application first before saving step');
    }

    patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getSingleStepApplication(applicationNo, step).pipe(
      tap((response: any) => {
        console.log('Single Step Application retrieved successfully', response);
        // console.log(response)
        // state.selectedApplication.phase2ApplicationSteps[stepName as keyof typeof state.selectedApplication.phase2ApplicationSteps]
        patchState({
          // applicationSteps: [...state.applicationSteps.filter(s => s.name !== step), { name: step, ...response.application }],
          // selectedApplication: response.application,
          loading: LoadingState.notLoading
        });
      }),
      catchError((error: any) => {
        console.error('Error retrieving Single Step Application', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }
      )
    );
  }


  @Action(GetPhase2ApplicationRecord)
  getPhase2ApplicationRecord({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetPhase2ApplicationRecord) {
    const state = getState();
    const { step, applicationNo } = action.payload;
    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.getPhase2ApplicationRecords(step, applicationNo).pipe(
      tap((response: { data: any }) => {
        patchState({ reviewPhase2: { ...state.reviewPhase2, ...response.data }, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(SubmitPro2Application)
  submitPro2Application({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: SubmitPro2Application) {
    const state = getState();
    const applicationId = action.payload;
    const examId = this.store.selectSnapshot(ProExamState.getSingleProExam)!.id;
    const selectedProApplication = state.selectedApplication;

    if (selectedProApplication == null) {
      throw new Error('Select application first before saving step');
    }

    patchState({ loading: LoadingState.loadingList });
    return this.applicationService.submitPro2Application(selectedProApplication, examId).pipe(
      tap((response: ProUserApplication) => {
        patchState({ selectedApplication: response, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        console.error('Error submitting Phase Two Application', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }


  @Action(OpenPhase2Application)
  openPhase2Application(state: StateContext<ApplicationsStateModel>, action: OpenPhase2Application) {
    state.patchState({ loading: LoadingState.loadingList });

    return this.applicationService.openPhase2Application(action.payload).pipe(
      tap((selectedApplication: ProUserApplication) => {
        if (selectedApplication && typeof selectedApplication !== 'string' && selectedApplication.id) {
          state.patchState({ selectedApplication, loading: LoadingState.notLoading });
        } else {
          state.patchState({ loading: LoadingState.notLoading });
        }
      }),
      catchError((error) => {
        state.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }



  @Action(GetAdminData)
  getAdminData({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetAdminData) {
    const state = getState();
    const applicationNo = action.payload.applicationNo;

    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.getAdminData(applicationNo).pipe(
      tap((response: { data: any }) => {
        // console.log('Admin data retrieved successfully', response);
        patchState({ selectedAdminData: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error retrieving admin data', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetApplicationRecord)
  getApplicationRecord({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetApplicationRecord) {
    const state = getState();
    const { step, applicationNo } = action.payload;

    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.getApplicationRecords(step, applicationNo).pipe(
      tap((response: { data: { adminData: any; steps: any } }) => {
        // console.log('Application record retrieved successfully', response);
        patchState({
          reviewApplication: {
            ...state.reviewApplication,
            application: [{ name: step, ...response.data }]
          } as ProApplicationReview,
          loading: LoadingState.notLoading
        });
      }),
      catchError((error: any) => {
        console.error('Error retrieving application record', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(SaveSinglePhaseTwoStep)
  SaveSinglePhaseTwoStep({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: SaveSinglePhaseTwoStep) {
    const state = getState();
    const { step, data, isFinished } = action.payload;
    const userId = this.store.selectSnapshot(AuthState.getCurrentUser)!.id;
    const examId = this.store.selectSnapshot(ProExamState.getSingleProExam)!.id;

    const selectedProApplication = state.selectedApplication;
    if (selectedProApplication == null) {
      throw new Error('Select application first before saving step');
    }

    patchState({ loading: LoadingState.loadingAddUpdate });

    return this.applicationService.saveSinglePhaseTwoStep(action.payload.step, action.payload.data).pipe(
      tap((response: { data: any }) => {
        console.log('Response from saving single phase two step:', response);

        console.log('Selected application before updating single step:', step);

        if (selectedProApplication.phase2ApplicationSteps) {
          let stepKey = Object.keys(selectedProApplication.phase2ApplicationSteps).find((key: string) =>
            (selectedProApplication.phase2ApplicationSteps as any)[key].name === step
          );

          if (stepKey == undefined) {
            stepKey = Object.keys(selectedProApplication.phase2ApplicationSteps).find((key: string) =>
              (selectedProApplication.phase2ApplicationSteps as any)[key].name.includes(step)
            );
            console.log('Step key found with partial match:', stepKey);
          }

          if (stepKey) {
            const currentStep = (selectedProApplication.phase2ApplicationSteps as any)[stepKey];
            console.log('Current step before update:', currentStep);
            console.log('Previous data to be added:', currentStep.userData);
            if (currentStep.userData && typeof currentStep.userData === 'object' && 'clinicalExperiences' in currentStep.userData && Array.isArray(currentStep.userData.clinicalExperiences)) {

              const updatedClinicalExperiences = [...currentStep.userData.clinicalExperiences, response.data];
              console.log('Updated clinicalExperiences after adding new item:', updatedClinicalExperiences);

              const updatedApplication = {
                ...selectedProApplication,
                phase2ApplicationSteps: {
                  ...selectedProApplication.phase2ApplicationSteps,
                  [stepKey]: {
                    ...currentStep,
                    userData: { ...currentStep.userData, clinicalExperiences: updatedClinicalExperiences }
                  }
                }
              };

              patchState({ loading: LoadingState.notLoading, selectedApplication: updatedApplication, singleCreatedReferee: response.data });

            } else if (Array.isArray(currentStep.userData)) {
              const updatedUserData = [...currentStep.userData, response.data];

              const updatedApplication = {
                ...selectedProApplication,
                phase2ApplicationSteps: {
                  ...selectedProApplication.phase2ApplicationSteps,
                  [stepKey]: {
                    ...currentStep,
                    userData: updatedUserData
                  }
                }
              };
              patchState({ loading: LoadingState.notLoading, selectedApplication: updatedApplication, singleCreatedReferee: response.data });
            }
          }
        }
      }),
      catchError((error: any) => {
        console.error('Error retrieving flag details', error);
        patchState({ loading: LoadingState.notLoading, singleCreatedReferee: null });

        throw error;
      })
    );
  }

  @Action(UpdateSinglePhaseTwoStep)
  UpdateSinglePhaseTwoStep({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: SaveSinglePhaseTwoStep) {
    const state = getState();
    const { step, data, isFinished } = action.payload;
    const userId = this.store.selectSnapshot(AuthState.getCurrentUser)!.id;
    const examId = this.store.selectSnapshot(ProExamState.getSingleProExam)!.id;
    const selectedProApplication = state.selectedApplication;
    if (selectedProApplication == null) {
      throw new Error('Select application first before saving step');
    }
    patchState({ loading: LoadingState.loadingAddUpdate });

    return this.applicationService.updateSinglePhaseTwoStep(action.payload.step, action.payload.data).pipe(
      tap((response: { data: any }) => {
        console.log('Single Phase Two Step updated successfully', response);

        if (selectedProApplication.phase2ApplicationSteps) {
          let stepKey = Object.keys(selectedProApplication.phase2ApplicationSteps).find((key: string) =>
            (selectedProApplication.phase2ApplicationSteps as any)[key].name === step
          );

          // Fallback: if stepKey not found, try partial matching
          if (!stepKey) {
            if (step === 'independent-practice') {
              stepKey = Object.keys(selectedProApplication.phase2ApplicationSteps).find((key: string) =>
                (selectedProApplication.phase2ApplicationSteps as any)[key].name === 'independent-practice-clinical-experience'
              );
            } else if (step === 'currency') {
              stepKey = Object.keys(selectedProApplication.phase2ApplicationSteps).find((key: string) =>
                (selectedProApplication.phase2ApplicationSteps as any)[key].name === 'currency-practice-clinical-experience'
              );
            }
          }

          if (stepKey) {
            const currentStep = (selectedProApplication.phase2ApplicationSteps as any)[stepKey];
            console.log('Current step before update:', currentStep);
            if (Array.isArray(currentStep.userData)) {
              const updatedUserData = currentStep.userData.map((item: any) =>
                item._id === response.data._id ? response.data : item
              );
              console.log('Updated userData after update:', updatedUserData);

              const updatedApplication = {
                ...selectedProApplication,
                phase2ApplicationSteps: {
                  ...selectedProApplication.phase2ApplicationSteps,
                  [stepKey]: {
                    ...currentStep,
                    userData: updatedUserData
                  }
                }
              };
              patchState({ selectedApplication: updatedApplication, loading: LoadingState.notLoading });
              return;
            }

            // Handle userData with clinicalExperiences array
            if (currentStep.userData && typeof currentStep.userData === 'object' &&
              'clinicalExperiences' in currentStep.userData &&
              Array.isArray(currentStep.userData.clinicalExperiences)) {

              const updatedClinicalExperiences = currentStep.userData.clinicalExperiences.map((item: any) =>
                item._id === response.data._id ? response.data : item
              );

              console.log('Updated clinicalExperiences after update:', updatedClinicalExperiences);

              const updatedApplication = {
                ...selectedProApplication,
                phase2ApplicationSteps: {
                  ...selectedProApplication.phase2ApplicationSteps,
                  [stepKey]: {
                    ...currentStep,
                    userData: {
                      ...currentStep.userData,
                      clinicalExperiences: updatedClinicalExperiences
                    }
                  }
                }
              };
              patchState({ selectedApplication: updatedApplication, loading: LoadingState.notLoading });
              return;
            }
          }
        }
        patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error updating single phase two step', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(DeleteReferees)
  deleteReferees({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: DeleteReferees) {
    const { applicationType, applicationIds } = action.payload;
    patchState({ loading: LoadingState.loadingAddUpdate });

    return this.applicationService.deleteReferees(applicationIds, applicationType).pipe(
      tap((response: { data: any }[]) => {
        const state = getState();
        const selectedProApplication = state.selectedApplication;

        if (selectedProApplication && selectedProApplication.phase2ApplicationSteps) {
          let stepKey = Object.keys(selectedProApplication.phase2ApplicationSteps).find((key: string) =>
            (selectedProApplication.phase2ApplicationSteps as any)[key].name === action.payload.applicationType
          );

          // Fallback: if stepKey not found, try partial matching
          if (!stepKey) {
            if (action.payload.applicationType === 'independent-practice') {
              stepKey = Object.keys(selectedProApplication.phase2ApplicationSteps).find((key: string) =>
                (selectedProApplication.phase2ApplicationSteps as any)[key].name === 'independent-practice-clinical-experience'
              );
            } else if (action.payload.applicationType === 'currency') {
              stepKey = Object.keys(selectedProApplication.phase2ApplicationSteps).find((key: string) =>
                (selectedProApplication.phase2ApplicationSteps as any)[key].name === 'currency-practice-clinical-experience'
              );
            }
          }

          if (stepKey) {
            const currentStep = (selectedProApplication.phase2ApplicationSteps as any)[stepKey];

            if (currentStep.userData && 'clinicalExperiences' in currentStep.userData && Array.isArray(currentStep.userData.clinicalExperiences)) {
              const updatedClinicalExperiences = currentStep.userData.clinicalExperiences.filter((item: any) =>
                !action.payload.applicationIds.includes(item._id)
              );

              const updatedApplication = {
                ...selectedProApplication,
                phase2ApplicationSteps: {
                  ...selectedProApplication.phase2ApplicationSteps,
                  [stepKey]: {
                    ...currentStep,
                    userData: {
                      ...currentStep.userData,
                      clinicalExperiences: updatedClinicalExperiences
                    }
                  }
                }
              };
              patchState({ selectedApplication: updatedApplication, loading: LoadingState.notLoading });
            }
          }
        }
        console.log('Referee deleted successfully', response);
        patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error deleting referee', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }





  @Action(GetFlagDetails)
  getFlagDetails({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetFlagDetails) {
    const state = getState();
    const applicationNo = action.payload;

    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.getFlagDetails(applicationNo).pipe(
      tap((response: { data: any }) => {
        // console.log('Flag details retrieved successfully', response);
        patchState({ flags: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error retrieving flag details', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(UpdateFlagResolution)
  updateFlagResolution({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: UpdateFlagResolution) {
    const state = getState();
    const applicationNo = action.payload;
    const flags = action.flags;

    patchState({ loading: LoadingState.loadingAddUpdate });

    return this.applicationService.updateFlagResolution(applicationNo, flags).pipe(
      tap((response: any) => {
        // console.log('Flag resolution updated successfully', response);
        patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error updating flag resolution', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetAllProExamApplications)
  getAllProExamApplications({ patchState }: StateContext<ApplicationsStateModel>, action: GetAllProExamApplications) {
    if (!action.payload.stealth) {
      patchState({ loading: LoadingState.loadingList });
    }
    return this.applicationService.getAllApplications(action.payload).pipe(
      tap((applications: any) => {
        if (action.payload.status === ProApplicationStatusEnum.Submitted) {
          patchState({ submittedApplications: applications, loading: LoadingState.notLoading });
          return;
        }
        patchState({ applications, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetAllProExamApplicationsSilent)
  getAllProExamApplicationsSilent({ patchState }: StateContext<ApplicationsStateModel>, action: GetAllProExamApplicationsSilent) {
    return this.applicationService.getAllApplications(action.payload).pipe(
      tap((applications: any) => {
        if (action.payload.status === ProApplicationStatusEnum.Submitted) {
          patchState({ submittedApplications: applications, loading: LoadingState.notLoading });
          return;
        }
        patchState({ applications, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetExportedApplications)
  getExportedApplications({ patchState }: StateContext<ApplicationsStateModel>, action: GetExportedApplications) {
    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.getExportedApplications(action.payload.examId).pipe(
      tap((response: any) => {
        // console.log('Exported applications retrieved successfully', response);
        patchState({ exportedApplications: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error retrieving exported applications', error);
        throw error;
      })
    );
  }

  @Action(GetAllApplicationSteps)
  getAllApplicationSteps({ getState, patchState }: StateContext<ApplicationsStateModel>, action: GetAllApplicationSteps) {
    const state = getState();
    const applicationId = action.payload;

    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.getAllApplicationSteps(applicationId).pipe(
      tap((response: any) => {
        // console.log('All application steps retrieved successfully', response);
        patchState({ applicationSteps: response, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error retrieving all application steps', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(ClearAllApplicationSteps)
  clearAllApplicationSteps({ getState, patchState }: StateContext<ApplicationsStateModel>, action: ClearAllApplicationSteps) {
    patchState({ applicationSteps: [], loading: LoadingState.notLoading });
  }

  @Action(GetEligibilityDetails)
  getEligibilityDetails({ patchState }: StateContext<ApplicationsStateModel>, action: GetEligibilityDetails) {
    patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getEligibilityDetails(action.payload).pipe(
      tap((response: any) => {
        // console.log('Eligibility details retrieved successfully', response);
        patchState({ eligibilityDetails: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error retrieving eligibility details', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(SetNeedClinicalDirector)
  setNeedClinicalDirector({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: SetNeedClinicalDirector) {
    patchState({ loading: LoadingState.notLoading });

    const state = getState();
    const selectedApplication = state.reviewApplication;
    const payload = {
      applicationId: selectedApplication?._id!,
      examId: action.payload.examId,
      needClinicalDirectorReview: action.payload.needClinicalDirectorReview
    }
    return this.applicationService.setNeedClinicalDirectorReview(payload).pipe(
      tap((response: any) => {
        patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error retrieving eligibility details', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(AddReferenceNote)
  addReferenceNote({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: AddReferenceNote) {
    patchState({ loading: LoadingState.notLoading });
    const state = getState();

    return this.applicationService.addReferenceNote(action.payload.step, action.payload.data).pipe(
      tap((response: any) => {
        patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error adding reference note', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  // ResendSingleReferenceEmail, ResendReferenceEmail, GetReferencesByApplicationId

  @Action(ResendSingleReferenceEmail)
  resendSingleReferenceEmail({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: ResendSingleReferenceEmail) {
    patchState({ loading: LoadingState.notLoading });
    const state = getState();
    return this.applicationService.resendSingleReferenceEmail(action.payload).pipe(
      tap((response: any) => {
        patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error resending single reference email', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }


  @Action(ResendReferenceEmail)
  resendReferenceEmail({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: ResendReferenceEmail) {
    patchState({ loading: LoadingState.notLoading });
    const state = getState();
    return this.applicationService.resendReferenceEmail(action.payload).pipe(
      tap((response: any) => {
        patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error resending reference email', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetAdminApplicationByNo)
  getAdminApplicationByNo({ patchState }: StateContext<ApplicationsStateModel>, action: GetAdminApplicationByNo) {
    patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getAdminApplicationByNo(action.payload.applicationNo).pipe(
      tap((response: any) => {
        console.log('Response Get Admin', response)
        patchState({ selectedApplication: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error retrieving admin application by number', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(ResendSingleRotationEquivalences)
  resendSingleRotationEquivalences({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: ResendSingleRotationEquivalences) {
    patchState({ loading: LoadingState.notLoading });
    const state = getState();
    return this.applicationService.resendSingleRotationEquivalences(action.payload).pipe(
      tap((response: any) => {
        patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error resending single rotation equivalences email', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(ClearSelectedProApplication)
  clearSelectedProApplication({ patchState }: StateContext<ApplicationsStateModel>) {
    patchState({ selectedApplication: null });
  }

}
